<template>
  <sheet-modal
    :visible="visible"
    :title="t('invite.bindSuperior')"
    :description="t('invite.modalDesc')"
    :show-close="false"
    :cancel-text="''"
    :confirm-text="checking ? t('common.loading') : binding ? t('invite.binding') : t('invite.bindButton')"
    :confirm-disabled="binding || checking || !codeTrimmed"
    :close-on-overlay="false"
    @confirm="handleBind"
  >
    <view class="bind-invite-body">
      <input
        class="bind-invite-input"
        type="text"
        v-model="code"
        :placeholder="t('invite.bindPlaceholder')"
        :disabled="binding"
        confirm-type="done"
        @confirm="handleBind"
      />
    </view>
  </sheet-modal>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import SheetModal from '@/components/sheet-modal/index.vue';
import { bindInvitation, getInvitation } from '@/api/network';
import { useWalletStore } from '@/store/wallet';

const { t } = useI18n();
const walletStore = useWalletStore();

const visible = ref(false);
const code = ref('');
const binding = ref(false);

/** 已确认绑定上级的地址；仅绑定成功或接口确认已有 parent 后关闭 */
let boundForAddress = '';
const checking = ref(false);
let pendingForce = false;
let ownInviteCode = '';
let defaultReferrerAddress = '';
let lastSuggestedCode = '';
let walletGeneration = 0;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let authPromptTimer: ReturnType<typeof setTimeout> | null = null;
/** 连续服务端/网络失败次数；达上限后停止自动轮询，等下次登录/切钱包再试 */
let serverFailStreak = 0;
let inviteBackoffMs = 2000;
const INVITE_BACKOFF_CAP_MS = 60_000;
const INVITE_SERVER_FAIL_STOP = 10;

const codeTrimmed = computed(() => String(code.value || '').trim());

const isEthAddress = (v: string) => /^0x[a-fA-F0-9]{40}$/.test(v);

const inviteErrorStatus = (e: unknown): number => {
  const status = Number((e as { statusCode?: number })?.statusCode);
  return Number.isFinite(status) ? status : 0;
};

/** 5xx / 无响应：后端不可用，不应当「token 未就绪」狂刷 */
const isServerDownError = (e: unknown) => {
  const status = inviteErrorStatus(e);
  return status === 0 || status === 408 || status === 429 || status >= 500;
};

const resetInviteRetryBudget = () => {
  serverFailStreak = 0;
  inviteBackoffMs = 2000;
};

const clearRetryTimer = () => {
  if (retryTimer) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
};

const clearAuthPromptTimer = () => {
  if (authPromptTimer) {
    clearTimeout(authPromptTimer);
    authPromptTimer = null;
  }
};

const scheduleRetry = (delayMs = 1800) => {
  clearRetryTimer();
  retryTimer = setTimeout(() => {
    retryTimer = null;
    if (walletStore.isAuthenticated) {
      void checkAndPrompt(true);
    }
  }, delayMs);
};

/** 邀请信息优先；都没有时仅使用本次接口返回的默认上级。 */
const resolveRefCode = (ownCode = ''): string => {
  const own = String(ownCode || ownInviteCode || '').trim().toLowerCase();
  const selfAddr = String(walletStore.address || '').trim().toLowerCase();
  const candidates: string[] = [
    String(walletStore.referrerCodeFromUrl || '').trim(),
  ];
  try {
    const stored = localStorage.getItem('authCode');
    if (stored) candidates.push(stored.trim());
  } catch {
    // ignore
  }
  try {
    const q = new URLSearchParams(window.location.search).get('ref');
    if (q) candidates.push(q.trim());
  } catch {
    // ignore
  }
  try {
    const hash = window.location.hash || '';
    if (hash.includes('?')) {
      const q = new URLSearchParams(hash.split('?')[1] || '').get('ref');
      if (q) candidates.push(q.trim());
    }
  } catch {
    // ignore
  }

  const isUsable = (raw: string) => {
    if (!raw) return false;
    const lower = raw.toLowerCase();
    if (own && lower === own) return false;
    if (selfAddr && lower === selfAddr) return false;
    return true;
  };

  for (const raw of candidates) {
    if (isUsable(raw)) return raw;
  }

  const fallback = defaultReferrerAddress;
  return isEthAddress(fallback) && !/^0x0{40}$/i.test(fallback) && isUsable(fallback) ? fallback : '';
};

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

const fetchInvitationWithRetry = async (retries = 6) => {
  let lastError: unknown;
  for (let i = 0; i < retries; i++) {
    // 硬门禁：未登录不请求
    if (!walletStore.isAuthenticated || !walletStore.accessToken) {
      await sleep(300 + i * 250);
      continue;
    }
    try {
      return await getInvitation();
    } catch (e) {
      lastError = e;
      // 后端宕机/网关错误：立刻失败，避免单次检查连打 6 次
      if (isServerDownError(e)) throw e;
      await sleep(350 + i * 300);
    }
  }
  if (lastError) throw lastError;
  throw new Error('invitation_unavailable');
};

/**
 * 仅在已登录时检查；未绑定上级则强制弹窗，不可手动关闭。
 * force：允许在已显示时重新查询 / 并发合并。
 */
const checkAndPrompt = async (force = false) => {
  if (!walletStore.isAuthenticated || !walletStore.address) {
    visible.value = false;
    return;
  }

  const addr = String(walletStore.address).toLowerCase();
  const generation = walletGeneration;
  const current = () => walletStore.isAuthenticated && generation === walletGeneration &&
    String(walletStore.address || '').toLowerCase() === addr;

  if (boundForAddress === addr) {
    visible.value = false;
    return;
  }
  if (visible.value && !force) return;
  // 后端已判定不可用：跳过强制重入，等登录态变化再重置
  if (serverFailStreak >= INVITE_SERVER_FAIL_STOP) return;

  if (checking.value) {
    if (force) pendingForce = true;
    return;
  }
  checking.value = true;

  try {
    const invitation = await fetchInvitationWithRetry();
    // 不能将旧钱包查询结果或默认地址写入新钱包的弹窗。
    if (!current()) return;

    ownInviteCode = String(invitation?.code || '').trim();
    defaultReferrerAddress = String(invitation?.defaultReferrerAddress || '').trim();

    resetInviteRetryBudget();

    // parent 有值 = 已绑定上级；仅 null/空 才弹
    const parent = invitation?.parent;
    if (parent != null && String(parent).trim() !== '') {
      boundForAddress = addr;
      visible.value = false;
      clearRetryTimer();
      return;
    }

    // 有 URL 邀请信息用邀请信息；否则预填默认上级地址
    const pref = resolveRefCode(ownInviteCode);
    if (!code.value.trim() || code.value === lastSuggestedCode ||
        code.value.trim().toLowerCase() === ownInviteCode.toLowerCase() ||
        code.value.trim().toLowerCase() === addr) {
      code.value = pref;
      lastSuggestedCode = pref;
    }

    visible.value = true;
  } catch (e) {
    if (!current()) return;
    defaultReferrerAddress = '';
    if (code.value === lastSuggestedCode) {
      code.value = resolveRefCode(ownInviteCode);
      lastSuggestedCode = code.value;
    }
    if (isServerDownError(e)) {
      serverFailStreak += 1;
      // 后端连续不可用：停止自动轮询，避免 500 刷屏
      if (serverFailStreak >= INVITE_SERVER_FAIL_STOP) {
        pendingForce = false;
        clearRetryTimer();
        return;
      }
      inviteBackoffMs = Math.min(INVITE_BACKOFF_CAP_MS, inviteBackoffMs * 2);
      scheduleRetry(inviteBackoffMs);
      return;
    }
    // 短暂未就绪（如 401 瞬时）：有限退避再试，不无限拉长
    serverFailStreak = 0;
    inviteBackoffMs = 2000;
    scheduleRetry(2000);
  } finally {
    checking.value = false;
    if (pendingForce && serverFailStreak < INVITE_SERVER_FAIL_STOP) {
      pendingForce = false;
      void checkAndPrompt(true);
    } else {
      pendingForce = false;
    }
  }
};

const markBoundAndClose = () => {
  visible.value = false;
  if (walletStore.address) {
    boundForAddress = String(walletStore.address).toLowerCase();
  }
};

const handleBind = async () => {
  if (binding.value || checking.value) return;
  const generation = walletGeneration;
  const value = codeTrimmed.value;
  if (!value) {
    uni.showToast({ title: t('invite.toasts.bindFailed'), icon: 'none' });
    return;
  }
  // 不能绑自己的邀请码或自己的钱包地址
  if (ownInviteCode && value.toLowerCase() === ownInviteCode.toLowerCase()) {
    uni.showToast({ title: t('invite.toasts.bindFailed'), icon: 'none' });
    return;
  }
  if (
    walletStore.address &&
    isEthAddress(value) &&
    value.toLowerCase() === String(walletStore.address).toLowerCase()
  ) {
    uni.showToast({ title: t('invite.toasts.bindFailed'), icon: 'none' });
    return;
  }
  if (!walletStore.isAuthenticated) {
    uni.showToast({ title: t('invite.toasts.bindFailed'), icon: 'none' });
    return;
  }

  binding.value = true;
  try {
    await bindInvitation(value);
    if (generation !== walletGeneration) return;
    markBoundAndClose();
    uni.showToast({ title: t('invite.toasts.bindSuccess'), icon: 'success' });
  } catch {
    if (generation !== walletGeneration) return;
    // 只提示成功/失败，不展示接口报错原文；已绑定则以查询结果为准
    try {
      const invitation = await getInvitation();
      if (generation !== walletGeneration) return;
      const parent = invitation?.parent;
      if (parent != null && String(parent).trim() !== '') {
        markBoundAndClose();
        uni.showToast({ title: t('invite.toasts.bindSuccess'), icon: 'success' });
        return;
      }
    } catch {
      // ignore
    }
    uni.showToast({ title: t('invite.toasts.bindFailed'), icon: 'none' });
  } finally {
    // 无论成败都清邀请码缓存，避免下次误用旧 ref 覆盖默认上级
    if (generation === walletGeneration) walletStore.clearReferrerCache();
    binding.value = false;
  }
};

const queueAuthPrompt = () => {
  clearAuthPromptTimer();
  // 等 layout / token 稳定后再查；仅登录态触发
  authPromptTimer = setTimeout(() => {
    authPromptTimer = null;
    if (walletStore.isAuthenticated) {
      void checkAndPrompt(true);
    }
  }, 500);
};

watch(
  () => [walletStore.isAuthenticated, walletStore.address] as const,
  ([ok, addr], prev) => {
    if (ok !== prev?.[0] || String(addr || '').toLowerCase() !== String(prev?.[1] || '').toLowerCase()) {
      walletGeneration++;
      defaultReferrerAddress = '';
      lastSuggestedCode = '';
      code.value = '';
      ownInviteCode = '';
      boundForAddress = '';
      visible.value = false;
      resetInviteRetryBudget();
    }
    if (!ok) {
      visible.value = false;
      ownInviteCode = '';
      boundForAddress = '';
      resetInviteRetryBudget();
      clearRetryTimer();
      clearAuthPromptTimer();
      return;
    }
    // 从未登录 → 已登录，或切换钱包：检查绑定（刷新恢复会话会走这条）
    const wasAuth = !!(prev && prev[0]);
    const prevAddr = prev?.[1];
    if (!wasAuth || String(prevAddr || '').toLowerCase() !== String(addr || '').toLowerCase()) {
      queueAuthPrompt();
    }
  },
  { immediate: true }
);

const onOpenEvent = (payload?: { code?: string; force?: boolean }) => {
  // 事件也可能在未登录时误发：硬门禁
  if (!walletStore.isAuthenticated || !walletStore.address) return;

  const incoming = String(payload?.code || '').trim();
  if (incoming) {
    const own = ownInviteCode.toLowerCase();
    const selfAddr = String(walletStore.address || '').toLowerCase();
    if (
      (!own || incoming.toLowerCase() !== own) &&
      incoming.toLowerCase() !== selfAddr
    ) {
      code.value = incoming;
      try {
        walletStore.setReferrerCode(incoming);
      } catch {
        // ignore
      }
    }
  } else {
    code.value = resolveRefCode();
    lastSuggestedCode = code.value;
  }
  void checkAndPrompt(true);
};

onMounted(() => {
  uni.$on('openBindInviteModal', onOpenEvent);
  // layout 挂载后若已登录，立即再查（覆盖 App/wallet emit 早于 listener）
  if (walletStore.isAuthenticated) {
    queueAuthPrompt();
    // 二次兜底：刷新后接口偶发未就绪
    setTimeout(() => {
      if (walletStore.isAuthenticated && !visible.value) {
        void checkAndPrompt(true);
      }
    }, 1600);
  }
});

onUnmounted(() => {
  uni.$off('openBindInviteModal', onOpenEvent);
  clearRetryTimer();
  clearAuthPromptTimer();
});
</script>

<style scoped lang="scss">
.bind-invite-body {
  padding: 8rpx 0 12rpx;
}

.bind-invite-input {
  width: 100%;
  height: 88rpx;
  padding: 0 28rpx;
  box-sizing: border-box;
  border-radius: 20rpx;
  background: #f3f5f8;
  border: 2rpx solid #e6e9ef;
  font-size: 28rpx;
  color: #171b24;
  line-height: 88rpx;
}
</style>
